import { CaretUpOutlined } from '@ant-design/icons';
import { dayjsWithTimezone, scrollToTop, twClassMerge, useScrollPosition } from '@healthhub/react-ui';
import { Divider, Form, Input, Typography } from 'antd';
import { useState } from 'react';
import type { KeyboardEvent } from 'react';

import { HealthHubIcon, SynapxeIcon, TelegramIcon, TikTopIcon } from '@shared/assets';
import { Button, MaxWidthContainer } from '@shared/components';
import { TurnstileWrapper } from '@shared/components/TurnstileWrapper';
import { PortalCheckbox } from '@shared/components/V2';
import { sitecoreHomeBaseUrl } from '@shared/configs/configs/config';
import { GOOGLE_URL, SITE_CORE_URL } from '@shared/constants/paths';
import useTurnstile from '@shared/hooks/useTurnstile';
import { useLazyGetReCaptchaQuery } from '@shared/services/validateCaptcha';
import { HEALTHHUB_TELEGRAM_URL, HEALTHHUB_TIKTOK_URL } from '@shared/utils/api/configs/URL';
import { emailRule } from '@shared/utils/validator';

import { ABOUT_LIST, PARTNERSHIP_LIST } from './constants';
import styles from './styles.module.css';
import { handleNewsLetterSubscriptionWithNewTab } from '../PageHeader/components/NewsLetterModal';

const { Text } = Typography;
const { useForm } = Form;

interface FormValues {
  acceptTou: boolean;
  email: string;
}

interface FormValues {
  acceptTou: boolean;
  email: string;
}
const PageFooter = () => {
  const [form] = useForm<FormValues>();
  const [getReCaptchaData, { isLoading }] = useLazyGetReCaptchaQuery();
  const [validationMode, setValidationMode] = useState<{ [key in keyof FormValues]: 'onBlur' | 'onChange' }>({
    acceptTou: 'onChange',
    email: 'onBlur',
  });

  const { resetTurnstile, turnstileRef, setTurnstileToken } = useTurnstile();

  const { y } = useScrollPosition();

  const onClickScrollTop = () => {
    scrollToTop();
  };

  const onKeyDownScrollTop = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      scrollToTop();
    }
  };

  const onSubmit = async () => {
    turnstileRef.current?.execute();
  };

  const onTurnstileSuccess = async (token: string) => {
    await handleNewsLetterSubscriptionWithNewTab({
      token,
      onValidate: async (token) => {
        const data = await getReCaptchaData({ ResponseToken: token, Version: 'v3' }).unwrap();
        return !!data;
      },
      email: form.getFieldValue('email'),
      onError: (error) => console.error('Turnstile execution failed:', error),
      onFinally: () => {
        form.resetFields();
        resetTurnstile();
      },
    });
  };

  const changeValidationMode = (fieldName: keyof FormValues) => {
    setValidationMode((prevState) => ({ ...prevState, [fieldName]: 'onChange' }));
  };

  return (
    <footer className="relative flex flex-col border-y-8 border-y-functional-haze bg-white" id="footer">
      <div className="flex justify-center px-5 py-10 shadow-portal-card md:px-24 lg:px-18">
        <MaxWidthContainer className="flex flex-col gap-13 md:flex-row md:flex-wrap md:gap-20 lg:flex-nowrap lg:justify-between">
          <div className="flex justify-center gap-13 md:gap-0">
            <div className="flex flex-col items-center gap-10 md:items-start">
              <a className="h-19 w-44" href={sitecoreHomeBaseUrl()}>
                <img alt="HealthHub" className="size-full" src={HealthHubIcon} />
              </a>
              <div className="flex flex-col items-center gap-6 md:items-start">
                <Text className="text-lg font-medium" lang="en">
                  Follow us:
                </Text>
                <div className="flex gap-5">
                  <a href={HEALTHHUB_TELEGRAM_URL} rel="noopener noreferrer" target="_blank">
                    <img alt="Telegram" src={TelegramIcon} />
                  </a>
                  <a href={HEALTHHUB_TIKTOK_URL} rel="noopener noreferrer" target="_blank">
                    <img alt="TikTok" src={TikTopIcon} />
                  </a>
                </div>
              </div>
            </div>
          </div>
          <Divider className="m-0 border-dashed border-secondary-grey-steel md:w-0 md:min-w-0 md:justify-self-center md:border-l md:border-t-0" />
          <Form className="flex flex-col gap-6 md:flex-1" form={form} onFinish={onSubmit}>
            <div className="flex flex-col gap-4">
              <Text className="text-lg font-semibold" lang="en">
                Sign up
              </Text>
              <Text className="text-base font-normal" lang="en">
                Join our newsletter mailing list and stay up to date with relevant health information, tips and more.
              </Text>
            </div>
            <div className="flex flex-col gap-4 md:flex-row">
              <Form.Item<FormValues>
                className="m-0 flex-1 [&_.ant-form-item-explain-error]:w-full"
                name="email"
                rules={[
                  { message: 'Please key in your email address.', required: true },
                  emailRule('Email contains an invalid email address.'),
                ]}
                validateTrigger={validationMode.email}
              >
                <Input
                  autoComplete="email"
                  className={twClassMerge('h-10 w-full rounded-lg', styles.input)}
                  onBlur={() => changeValidationMode('email')}
                  placeholder="Email your email address"
                />
              </Form.Item>
              <Button
                className="json-checked h-10 bg-primary-blue-grey-3 py-0"
                htmlType="submit"
                lang="en"
                loading={isLoading}
              >
                Sign up now
              </Button>
            </div>
            <Form.Item<FormValues>
              className="m-0 [&_.ant-form-item-explain-error]:w-full"
              name="acceptTou"
              rules={[
                {
                  validator: (_, value) =>
                    value
                      ? Promise.resolve()
                      : Promise.reject(new Error('Please confirm you have read the Privacy Policy.')),
                },
              ]}
              valuePropName="checked"
            >
              <PortalCheckbox className={twClassMerge('text-xs', styles.checkBox)}>
                By subscribing to our e-Newsletter, you have accepted HealthHub&apos;s{' '}
                <a
                  className="text-primary-green-4 underline"
                  href={`${sitecoreHomeBaseUrl()}${SITE_CORE_URL.termsOfUse}`}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Terms of Use
                </a>{' '}
                and acknowledge that you have read and understood our{' '}
                <a
                  className="text-primary-green-4 underline"
                  href={`${sitecoreHomeBaseUrl()}${SITE_CORE_URL.privacyPolicy}`}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Privacy Policy
                </a>
                .
              </PortalCheckbox>
            </Form.Item>
            <TurnstileWrapper
              onSuccess={onTurnstileSuccess}
              options={{
                execution: 'execute',
                appearance: 'interaction-only',
              }}
              setTurnstileToken={setTurnstileToken}
              turnstileRef={turnstileRef}
            />
            <p className="p-0 text-xs text-functional-iron" lang="en">
              This site is protected by reCAPTCHA and the Google{' '}
              <a className="underline" href={GOOGLE_URL.privacyPolicy} rel="noopener noreferrer" target="_blank">
                Privacy Policy
              </a>{' '}
              and{' '}
              <a className="underline" href={GOOGLE_URL.termsOfService} rel="noopener noreferrer" target="_blank">
                Terms of Service
              </a>{' '}
              apply.
            </p>
          </Form>
          <div className="mx-auto flex flex-col gap-4 lg:flex-1">
            <Text className="text-lg font-semibold" lang="en">
              In partnership with
            </Text>
            <div className="grid w-full grid-cols-3 justify-items-center gap-6 md:grid md:grid-cols-6 lg:grid lg:grid-cols-3">
              {PARTNERSHIP_LIST.map(({ alt, icon, url }) => (
                <a href={url} key={url} rel="noopener noreferrer" target="_blank">
                  <img alt={alt} src={icon} />
                </a>
              ))}
            </div>
          </div>
        </MaxWidthContainer>
      </div>
      <div className="flex justify-center px-5 py-12 md:p-10">
        <button
          className={twClassMerge(
            'fixed z-footer transition bottom-[20vh] flex gap-1 duration-500 translate-x-full right-0 rounded-l-3xl bg-functional-black px-2.5 py-2 text-start text-xs text-white',
            y > 50 && 'translate-x-0'
          )}
          lang="en"
          onClick={onClickScrollTop}
          onKeyDown={onKeyDownScrollTop}
          type="button"
        >
          Top
          <CaretUpOutlined className="text-base" />
        </button>
        <MaxWidthContainer className="flex flex-col gap-10">
          <div className="flex flex-col gap-6 md:flex-row md:gap-12">
            {ABOUT_LIST.map(({ title, url }) => (
              <a
                className="text-sm font-normal text-primary-blue-grey-3"
                href={`${sitecoreHomeBaseUrl()}${url}`}
                key={title}
                lang="en"
                rel="noopener noreferrer"
                target="_blank"
              >
                {title}
              </a>
            ))}
          </div>
          <div className="flex flex-col items-center gap-6 md:flex-row md:justify-between">
            <img alt="" src={SynapxeIcon} />
            <Text className="max-w-56 text-center text-xs text-functional-iron md:max-w-none" lang="en">
              © {dayjsWithTimezone().year()} Ministry of Health Singapore. All rights reserved.
            </Text>
          </div>
        </MaxWidthContainer>
      </div>
    </footer>
  );
};

export default PageFooter;
